import { PayloadAction } from '@reduxjs/toolkit';
import {
  ConfigSaga,
  ConfigSelectors,
  FirebaseActions,
  IDomainCalculation,
  IterableActions,
  IterableSelectors,
  LanguageService,
  NotificationActions,
  ProgressActions,
  StatsActions,
  logger,
  preloadStatsSaga,
  waitForExperimentsFeature,
  waitForFirebaseReadySaga,
  waitForFullProgressSaga,
} from '@web_opxp/features';
import { all, call, fork, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { AssessmentRoute, getAssessmentConfigForCycle } from '../assessment';
import { MOOD_ID, NEWS_LETTER_STAT_ID } from '../base/constants';
import { redirectInternal } from '../base/historyService';
import { IAssessmentConfigOPXP } from '../base/types';
import { ChallengeSelectors } from '../challenge';
import { CyclesActions, CyclesSelectors } from '../cycles';
import { EvaluationActions, EvaluationSelectors } from '../evaluation';
import { HighscoresActions } from '../highscores';
import { SessionActions } from '../session';
import { DashboardActions } from './slice';

export function* preloadCycles() {
  const cyclesLoading: boolean = yield select(CyclesSelectors.isCyclesLoading);
  if (cyclesLoading) {
    yield take(CyclesActions.cyclesReady);
  }
}

export function* openDashboardSaga() {
  // wait until cycles state are loaded
  yield call(preloadCycles);
  // wait until firebase remote config is loaded
  yield call(waitForFirebaseReadySaga);
  // wait until user stats are loaded
  yield call(preloadStatsSaga);
  // preload last domain calculation
  yield call(preloadLastEvaluation);
  // make sure experiments are loaded
  yield call(waitForExperimentsFeature);
  // preload challenge highscores if necessary
  yield call(preloadTodaysChallengeHighscores);
  // refresh session state
  yield put(SessionActions.loadSession());
  // track dashboard open on iterable
  yield fork(trackDashboardOpenOnIterable);
}

const MAX_CYCLES_TO_LOAD = 5;

export function* preloadLastEvaluation() {
  yield call(waitForEvaluationFeatureReady);
  let cycleId: number = yield select(CyclesSelectors.getCurrentCycleId);
  let evaluationLoaded = false;
  let cyclesLoaded = 0;
  while (cycleId > 1 && !evaluationLoaded && cyclesLoaded < MAX_CYCLES_TO_LOAD) {
    yield put(EvaluationActions.getCycleEvaluation(cycleId));
    const result: PayloadAction<IDomainCalculation[]> = yield take(EvaluationActions.cycleEvaluationResult);
    evaluationLoaded = result.payload && result.payload.length > 0;
    cycleId -= 1;
    cyclesLoaded += 1;
  }
}

export function* preloadTodaysChallengeHighscores() {
  const challengeActive = yield select(ChallengeSelectors.isChallengeActive);

  if (!challengeActive) {
    return;
  }

  const todaysChallengeWorkoutId: number | null = yield select(ChallengeSelectors.getTodaysChallengeWorkoutId);

  if (!todaysChallengeWorkoutId) {
    return;
  }

  yield call(ConfigSaga.preloadConfig);

  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, todaysChallengeWorkoutId);

  yield put(HighscoresActions.loadHighscores(exerciseId, true));
}

export function* startAssessmentSaga(action: PayloadAction<boolean>) {
  const cfq = action.payload;
  logger(`startAssessmentSaga cfq=${cfq}`);
  // ensure cycles are loaded
  yield call(preloadCycles);
  // ensure firebase is loaded
  yield call(waitForFirebaseReadySaga);
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const nextCycleId = cycleId + 1;
  logger(`startAssessmentSaga nextCycleId=${nextCycleId}`);
  const assessmentConfig: IAssessmentConfigOPXP = yield call(getAssessmentConfigForCycle, nextCycleId);
  // cycle and assessment creation will be skipped by cycle feature if already running
  yield put(CyclesActions.createAssessment(assessmentConfig, cfq));
  yield take(CyclesActions.assessmentCreated);
  logger('startAssessmentSaga reloading session');
  yield put(SessionActions.reloadSession());
  logger('startAssessmentSaga redirect to assessment');
  return redirectInternal(AssessmentRoute);
}

export function* waitForEvaluationFeatureReady() {
  const isReady = yield select(EvaluationSelectors.isFeatureReady);
  if (!isReady) {
    yield take(EvaluationActions.evaluationReady);
  }
}

export function* cloneLastSessionSaga() {
  logger('cloneLastSessionSaga');
  yield put(CyclesActions.cloneLastSession());
  yield take(CyclesActions.cloneCycleFinished);
  yield put(SessionActions.reloadSession());
}

export function* cloneLastCycleSaga() {
  logger('cloneLastCycleSaga');
  yield put(CyclesActions.cloneCycle());
  yield take(CyclesActions.cloneCycleFinished);
  yield put(SessionActions.reloadSession());
}

export function* skipAssessmentSaga(action: PayloadAction<boolean | undefined>) {
  const addSession = action.payload;
  yield call(preloadCycles);
  logger(`skipAssessmentSaga addSession:${addSession}`);
  // skip current assessment session if it was already started
  const sessionFinished = yield select(CyclesSelectors.isCurrentSessionFinished);
  if (!sessionFinished) {
    yield put(CyclesActions.finishCurrentSession());
    yield take(CyclesActions.currentSessionFinished);
    yield call(createNewTrainingCycleSaga);
    return;
  }
  // calculate how much sessions must be created
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);

  if (cycleId === 1 || addSession) {
    // clone one last session
    return yield call(cloneLastSessionSaga);
  }
  // otherwise clone the whole last cycle
  yield call(cloneLastCycleSaga);
}

export function* createNewTrainingCycleSaga(sessions = 0) {
  yield put(CyclesActions.createTraining(sessions));
  // TODO: implement an error handling here (BW-7361)
  yield take([CyclesActions.trainingCreated, CyclesActions.createTrainingFailed]);
  yield put(SessionActions.reloadSession());
}

export function* saveMoodSaga(action: PayloadAction<number>) {
  const result = action.payload;
  // we must wait for progress feature to finish loading process before we can save anything
  yield call(waitForFullProgressSaga);

  const workoutId = MOOD_ID;

  // save mood to progress
  const cycleId = yield select(CyclesSelectors.getCurrentCycleId);
  const sessionId = yield select(CyclesSelectors.getCurrentSessionId);
  const variant = yield select(ConfigSelectors.getConfigVariant);
  const version = yield select(ConfigSelectors.getConfigVersion);

  yield put(ProgressActions.saveProgressScore(variant, version, cycleId, sessionId, workoutId, result));
}

export function* subscribeToNewsletterSaga() {
  const t = LanguageService.getTFunction();
  yield put(StatsActions.updateBooleanStat(NEWS_LETTER_STAT_ID, true));
  yield put(StatsActions.saveStatsQueue());

  yield take(StatsActions.saveStatsQueueSuccess);
  yield put(NotificationActions.showSuccess(t('Dashboard.SuccessMessage')));
  yield put(FirebaseActions.analyticsEventAction({ eventName: 'marketing_optin_button' }));
}

export function* trackDashboardOpenOnIterable() {
  const isReady = yield select(IterableSelectors.isStarted);
  if (!isReady) {
    yield take(IterableActions.iterableStarted);
  }
  yield put(IterableActions.trackDashboardOpen());
}

export default function* dashboardWatcher() {
  yield all([
    takeEvery(DashboardActions.openDashboard, openDashboardSaga),
    takeLeading(DashboardActions.startAssessment, startAssessmentSaga),
    takeLeading(DashboardActions.skipAssessment, skipAssessmentSaga),
    takeLeading(DashboardActions.saveMood, saveMoodSaga),
    takeLatest(DashboardActions.subscribeToNewsletter, subscribeToNewsletterSaga),
  ]);
}
